import { useCallback, useEffect, useState } from "react";
import {
  Bell,
  Settings,
  AtSign,
  MessageSquare,
  ListChecks,
  Mic,
  CheckCheck,
} from "lucide-react";
import api from "@/lib/api";
import { relTime } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import NotificationSettings from "@/components/NotificationSettings";

const KIND_ICON = {
  message: MessageSquare,
  mention: AtSign,
  thread_reply: MessageSquare,
  task_assigned: ListChecks,
  task_completed: ListChecks,
  meeting: Mic,
};

export default function NotificationBell({ channels = [], onNavigate }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items);
      setUnread(r.data.unread);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  }, []);

  useEffect(() => {
    load();
    const int = setInterval(load, 12000);
    return () => clearInterval(int);
  }, [load]);

  const markAll = async () => {
    setUnread(0);
    setItems((list) => list.map((n) => ({ ...n, read: true })));
    await api.post("/notifications/read-all").catch((err) => console.error(err));
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setUnread((u) => Math.max(0, u - 1));
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      await api.post(`/notifications/${n.id}/read`).catch((err) => console.error(err));
    }
    onNavigate?.(n);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="notification-bell-button"
            className="relative w-9 h-9 rounded-full bg-[#1E2029] border border-[#2E3242] flex items-center justify-center text-gray-400 hover:text-red-300 hover:border-red-900/50 transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span
                data-testid="notification-unread-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full ruby-gradient text-white text-[10px] font-bold flex items-center justify-center border border-[#121318]"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[min(92vw,380px)] p-0 bg-[#121318] border-[#2E3242] text-gray-200"
          data-testid="notification-panel"
        >
          <div className="flex items-center gap-2 px-4 h-12 border-b border-[#2E3242]">
            <p className="font-display font-bold text-sm text-gray-100">Notifications</p>
            {unread > 0 && (
              <button
                data-testid="mark-all-read-button"
                onClick={markAll}
                className="ml-auto flex items-center gap-1 text-[11px] text-red-300 hover:text-red-200"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
            <button
              data-testid="open-notification-settings"
              onClick={() => {
                setOpen(false);
                setSettingsOpen(true);
              }}
              className={`${unread > 0 ? "" : "ml-auto"} text-gray-500 hover:text-red-300`}
              aria-label="Notification settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 && (
              <p className="text-sm text-gray-600 text-center py-10 px-6">
                Nothing yet. Mentions, tasks and new recordings will show up here.
              </p>
            )}
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#1E2029] hover:bg-[#1E2029] transition-colors ${
                    n.read ? "opacity-60" : ""
                  }`}
                >
                  <span
                    className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${
                      n.read ? "bg-[#1E2029] text-gray-500" : "ruby-gradient text-white"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-100 leading-snug">
                      {n.title}
                    </span>
                    <span className="block text-xs text-gray-400 line-clamp-2 mt-0.5">{n.body}</span>
                    <span className="block text-[11px] text-gray-600 mt-1">{relTime(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <NotificationSettings open={settingsOpen} onOpenChange={setSettingsOpen} channels={channels} />
    </>
  );
}
